import {
  SearchInput,
  SearchInputTypes,
  Shadow,
  Tabs,
  TabsTypes,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import { CartSectionStatus } from '../../utils/types';
import { BatchTicketCustomersSelected } from '../Customer/components/Selected';

import { ClearSelectionButton, FilterButton, OrderButton } from './button';
import { SectionHeaderWrapper } from './header';

interface SectionWrapperProps {
  title: string;
  itemCount: number;
  filterCount: number;
  tableComponent: React.ReactNode;
  legendComponent: React.ReactNode;
  orderButtonIsDisabled?: boolean;
  filterButtonIsDisabled?: boolean;
  clearSelectionButtonIsDisabled?: boolean;
  onPressOrderButton?: () => void;
  onPressFilterButton: () => void;
  onPressClearSelectionButton: () => void;
  onPressBasketButton: () => void;
  itemTabsCategoriesProps?: TabsTypes.Props;
  searchInputProps: SearchInputTypes.Props;
  status?: CartSectionStatus;
  onEdit?: () => void;
  onAdd?: () => void;
}
interface WrapperProps {
  children: React.ReactNode;
}

export const DesktopWrapper: React.FC<WrapperProps> = ({ children }) => (
  <Shadow level={1} borderTopRadius={BoxRadius.sm}>
    {children}
  </Shadow>
);

export const MobileWrapper: React.FC<WrapperProps> = ({ children }) => (
  <Box borderTopRadius={BoxRadius.sm}>{children}</Box>
);

export const SectionWrapper: React.FC<SectionWrapperProps> = ({
  itemCount,
  filterCount,
  tableComponent,
  legendComponent,
  searchInputProps,
  clearSelectionButtonIsDisabled = false,
  filterButtonIsDisabled = false,
  orderButtonIsDisabled = false,
  onPressClearSelectionButton,
  onPressFilterButton,
  onPressOrderButton,
  onPressBasketButton,
  itemTabsCategoriesProps,
  title,
  status = 'start',
  onEdit = () => null,
  onAdd = () => null,
}) => {
  const Wrapper = useBreakpointValue({
    base: MobileWrapper,
    sm: DesktopWrapper,
  });

  const isMobile = useBreakpointValue({ base: true, sm: false });

  const statusIsStart = status === 'start';
  const statusIsComplete = status === 'complete';

  if (isMobile && statusIsComplete) {
    return <></>;
  }

  return (
    <Wrapper>
      <VStack
        w="full"
        bg={{ base: 'transparent', sm: 'neutral.white' }}
        borderTopRadius={BoxRadius.sm}
        space={{ base: Spacing['inline.xxs'] }}>
        {!isMobile && (
          <SectionHeaderWrapper
            itemCount={itemCount}
            onAdd={onAdd}
            onEdit={onEdit}
            onPressBasketButton={onPressBasketButton}
            statusIsComplete={statusIsComplete}
            statusIsStart={statusIsStart}
            title={title}
          />
        )}
        {statusIsStart && (
          <VStack px={{ sm: Spacing['inline.xxs'] }} space={{ base: Spacing['inline.xxs'] }}>
            {itemTabsCategoriesProps && (
              <HStack alignItems="center" space={Spacing['inline.xxxs']} flex={1}>
                <Tabs
                  {...itemTabsCategoriesProps}
                  variant={itemTabsCategoriesProps.variant ?? 'primary'}
                />
              </HStack>
            )}
            <HStack alignItems="center" justifyContent="space-between">
              <SearchInput isMobile={isMobile} {...searchInputProps} />
              {!isMobile && (
                <FilterButton
                  count={filterCount}
                  isDisabled={filterButtonIsDisabled}
                  onPress={onPressFilterButton}
                  isMobile={isMobile}
                />
              )}
            </HStack>
            <HStack
              alignItems="center"
              justifyContent={'space-between'}
              space={Spacing['inline.xxs']}>
              {isMobile && onPressOrderButton && (
                <OrderButton isDisabled={orderButtonIsDisabled} onPress={onPressOrderButton} />
              )}
              {isMobile && (
                <FilterButton
                  count={filterCount}
                  isDisabled={filterButtonIsDisabled}
                  onPress={onPressFilterButton}
                  isMobile={isMobile}
                />
              )}
              {isMobile && (
                <ClearSelectionButton
                  isDisabled={clearSelectionButtonIsDisabled}
                  onPress={onPressClearSelectionButton}
                />
              )}
            </HStack>
          </VStack>
        )}
        {statusIsStart && !isMobile && (
          <VStack space={Spacing['inline.xxxs']}>
            <HStack
              alignItems={'center'}
              justifyContent={'space-between'}
              px={Spacing['inline.xxs']}>
              {legendComponent}
              <ClearSelectionButton
                isDisabled={clearSelectionButtonIsDisabled}
                onPress={onPressClearSelectionButton}
              />
            </HStack>
          </VStack>
        )}
        {statusIsStart && tableComponent}
        {statusIsComplete && <BatchTicketCustomersSelected />}
      </VStack>
    </Wrapper>
  );
};
